import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useApp } from "../context/AppContext";
import { supabase } from "../lib/supabase.js";

const FALLBACK    = "/logo192.png";
const IS_UUID     = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const IS_OBJECTID = /^[0-9a-fA-F]{24}$/;

const ArtistProfile = () => {
  const { t, lang } = useApp();
  const isAr = lang === "ar";
  const { id } = useParams();
  const [artist, setArtist] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    const load = async () => {
      try {
        if (IS_UUID.test(id)) {
          const { data } = await supabase.from("artists").select("*").eq("id", id).single();
          if (data) { setArtist({ ...data, _source: "supabase" }); setStatus("ready"); return; }
        }
        if (IS_OBJECTID.test(id)) {
          const res = await axios.get(`/api/artists/${id}`);
          if (res.data.artist) { setArtist({ ...res.data.artist, _source: "mongo" }); setStatus("ready"); return; }
        }
        const [supaRes, mongoRes] = await Promise.allSettled([
          supabase.from("artists").select("*").eq("id", id).single(),
          axios.get(`/api/artists/${id}`),
        ]);
        if (supaRes.status === "fulfilled" && supaRes.value.data) {
          setArtist({ ...supaRes.value.data, _source: "supabase" }); setStatus("ready");
        } else if (mongoRes.status === "fulfilled" && mongoRes.value.data?.artist) {
          setArtist({ ...mongoRes.value.data.artist, _source: "mongo" }); setStatus("ready");
        } else {
          setStatus("error");
        }
      } catch {
        setStatus("error");
      }
    };
    if (id) load();
    else setStatus("error");
  }, [id]);

  if (status === "loading") return <div className="empty-state">Loading…</div>;
  if (status === "error" || !artist)
    return (
      <div className="empty-state">
        <p>Artist not found.</p>
        <Link className="btn btn-primary" to="/artists">{t("allArtists")}</Link>
      </div>
    );

  const fromSupa = artist._source === "supabase";
  const name     = isAr ? (artist.name_ar      || artist.name)       : artist.name;
  const spec     = isAr ? (artist.specialty_ar  || artist.specialty)  : artist.specialty;
  const bio      = isAr ? (artist.bio_ar        || artist.bio         || artist.biography)
                        : (artist.bio           || artist.biography   || "");
  const intro    = isAr ? (artist.intro_ar      || artist.intro)
                        : (artist.intro         || artist.Intro       || "");
  const avatar   = fromSupa ? artist.photo_url  : (artist.profileImage || FALLBACK);
  const works    = Array.isArray(artist.images) ? artist.images : [];

  return (
    <>
      <div className="page-band">
        <div className="container" style={{ display: "flex", gap: 24, alignItems: "center", flexWrap: "wrap" }}>
          <img className="acard-avatar" style={{ width: 120, height: 120 }}
               src={avatar || FALLBACK} alt={name}
               onError={(e) => { e.currentTarget.src = FALLBACK; }} />
          <div>
            <h1 style={{ marginBottom: 6 }}>{name}</h1>
            {spec && <div style={{ color: "var(--brand)", fontWeight: 600 }}>{spec}</div>}
            {artist.country && (
              <div className="muted"><i className="fa-solid fa-location-dot" /> {artist.country}</div>
            )}
          </div>
        </div>
      </div>

      <section className="section container">
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 28, maxWidth: 820 }}>
          {intro && <p className="lead" style={{ maxWidth: "none" }}>{intro}</p>}
          {bio && (
            <div>
              <h2 className="section-title" style={{ fontSize: "1.6rem" }}>{t("bio")}</h2>
              <p className="muted" style={{ lineHeight: 1.8, whiteSpace: "pre-line" }}>{bio}</p>
            </div>
          )}

          {works.length > 0 && (
            <div>
              <h2 className="section-title" style={{ fontSize: "1.6rem" }}>{t("selectedWork")}</h2>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12 }}>
                {works.map((src, i) => (
                  <img key={i} src={src} alt={`work ${i + 1}`}
                    style={{ width: "100%", borderRadius: 12, border: "1px solid var(--line)" }} />
                ))}
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: 18, flexWrap: "wrap", color: "var(--muted)" }}>
            {artist.website   && <a href={artist.website}               target="_blank" rel="noreferrer"><i className="fa-solid fa-globe"    /> Website</a>}
            {artist.email     && <a href={`mailto:${artist.email}`}                                     ><i className="fa-solid fa-envelope"  /> {artist.email}</a>}
            {artist.telephone && <a href={`tel:${artist.telephone}`}                                    ><i className="fa-solid fa-phone"     /> {artist.telephone}</a>}
          </div>

          <Link className="btn btn-outline" to="/artists" style={{ width: "fit-content" }}>
            <i className="fa-solid fa-arrow-left" /> {t("allArtists")}
          </Link>
        </div>
      </section>
    </>
  );
};

export default ArtistProfile;
